/** @type {import('tailwindcss').Config} */
export default {
  content: ["./index.html", "./src/**/*.{js,ts,jsx,tsx}", "./lib/**/*.{js,ts,jsx,tsx}"],
  theme: {
    extend: {
      colors: {
        content: {
          base: "#FFFFFF",
          soft: "#A8A29E",
          disabled: "#78716C",
        },
        informative: {
          base: "#23B4C9",
          50: "#ECFEFF",
          100: "#CFFAFE",
          200: "#A5F3FC",
          300: "#67E8F9",
          400: "#22D3EE",
          500: "#06B6D4",
          600: "#0891B2",
          700: "#0E7490",
          800: "#155E75",
          900: "#164E63",
          950: "#083344",
        },
        warning: {
          base: "#D67D39",
          soft: "#A65028",
          soft2: "#E8BC89",
          strong: "#E8BC89",
          bold: "#F9EDDB",
          subtle: "#6C3722",
          50: "#FCF7F0",
          100: "#F9EDDB",
          200: "#F1D7B7",
          300: "#E8BC89",
          400: "#E09F64",
          500: "#D67D39",
          600: "#C8672E",
          700: "#A65028",
          800: "#854027",
          900: "#6C3722",
          950: "#3A1A10",
        },
        error: {
          base: "#EF4444",
          soft: "#B91C1C",
          strong: "#FCA5A5",
          bold: "#FEE2E2",
          subtle: "#7F1D1D",
          50: "#FEF2F2",
          100: "#FEE2E2",
          200: "#FECACA",
          300: "#FCA5A5",
          400: "#F87171",
          500: "#EF4444",
          600: "#DC2626",
          700: "#B91C1C",
          800: "#991B1B",
          900: "#7F1D1D",
          950: "#450A0A",
        },
        success: {
          base: "#15A462",
          soft: "#14814F",
          strong: "#85F0BE",
          bold: "#DCFCEC",
          subtle: "#135438",
          50: "#F0FDF6",
          100: "#DCFCEC",
          200: "#BAF8DA",
          300: "#85F0BE",
          400: "#49DF99",
          500: "#21C97B",
          600: "#15A462",
          700: "#14814F",
          800: "#156642",
          900: "#135438",
          950: "#052E1D",
        },
        darkBrand: {
          50: "#FAFAF9",
          100: "#F5F5F4",
          200: "#E7E5E4",
          300: "#D6D3D1",
          400: "#A8A29E",
          500: "#78716C",
          600: "#57534E",
          700: "#44403C",
          800: "#292524",
          900: "#1C1917",
          950: "#0C0A09",
        },
        accentBrand: {
          50: "#FEF1F7",
          100: "#FEE5F1",
          200: "#FFCBE4",
          300: "#FFA1CE",
          400: "#FF67AA",
          500: "#F92B81",
          600: "#EA1865",
          700: "#CC0A4C",
          800: "#A80C3F",
          900: "#8C0F37",
          950: "#56011D",
        },
        brand: {
          50: "#FCF3FA",
          100: "#F9EAF6",
          200: "#F5D5EE",
          300: "#EEB3E1",
          400: "#EEB3E1",
          500: "#E383CB",
          600: "#C33F99",
          700: "#B03083",
          800: "#8B2967",
          900: "#752658",
          950: "#461132",
        },
        stroke: {
          base: "#44403C",
          soft: "#292524",
          strong: "#57534E",
          error: "#EF4444",
        },
        commonAlpha: {
          base: "rgba(255, 255, 255, 0.4)",
          soft: "rgba(255, 255, 255, 0.1)",
          softer: "rgba(255, 255, 255, 0.04)",
          inverseBase: "rgba(0, 0, 0, 0.4)",
          inverseStrong: "rgba(0, 0, 0, 0.6)",
        },
        // components
        referBalls: {
          basket1BgStart: "#FBBF24",
          basket1BgStop: "#FBBF24",
          basket1Label: "#FBBF24",
          basket2BgStart: "#E09F64",
          basket2BgStop: "#E09F64",
          basket2Label: "#E09F64",
          basket3BgStart: "#8A32EB",
          basket3BgStop: "#8A32EB",
          basket3Label: "#BA83FD",
          basket4BgStart: "#00BCFF",
          basket4BgStop: "#00BCFF",
          basket4Label: "#00BCFF",
          basket5BgStart: "#59549F",
          basket5BgStop: "#59549F",
          basket5Label: "#B8AEDA",
          basketNormalBgStart: "#45408D",
          basketNormalBgStop: "#45408D",
          basketNormalLabel: "#8E89D1",
        },
      },
      backgroundImage: {
        header: "radial-gradient(133.74% 100.44% at 100% 50%, #292524 0%, #1C1917 100%)",
        dialog: "radial-gradient(69.19% 53.57% at 50% 0%, #44403C 0%, #1C1917 100%)",
        homeBtn: "linear-gradient(180deg, #44403C 0%, #292524 50.48%, #44403C 100%)",
        grdPrimary: "radial-gradient(50% 100% at 50% 0%, #F92B81 0%, #CC0A4C 100%)",
        grdPrimaryAlt: "linear-gradient(180deg, #8C0F37 0%, #A80C3F 100%)",
        grdSecondary: "linear-gradient(180deg, #44403C 0%, #292524 100%)",
        grdSecondaryAlt: "linear-gradient(180deg, #312D6F 0%, #45408D 100%)",
        bottomMenu: "radial-gradient(33.48% 103.75% at 52.2% 0%, #752658 0%, #1C1917 100%)",
        bottomNavBtnRound: "linear-gradient(180deg, #FF67AA 0%, #A80C3F 100%)",
        bottomNavBtnRoundRimLight: "radial-gradient(50% 25% at 50% 100%, #FDE68A 0%, rgba(86, 1, 29, 0.00) 100%)",
        bottomNavBtnIndicatorActive: "radial-gradient(44.58% 50% at 50% 100%, #3c3c3c 0%, #ffffff00 100%)",
        bottomNavBtnIndicator: "",
        pageTitle: "linear-gradient(180deg, #FFF 19.11%, #D6D3D1 71.43%)",
        gradientTextBase: "linear-gradient(180deg, #FFF 19.11%, #D6D3D1 71.43%)",
        tabNavSecondary: "linear-gradient(180deg, #44403C 0%, #292524 100%)",
        referFriendTopSection:
          "radial-gradient(73.33% 38.02% at 73.33% 58.07%, #9e9da236 0%, rgba(0, 0, 0, 0.00) 100%)",
        progressBarTray: "linear-gradient(180deg, #1C1917 0%, #292524 100%)",
        timerProgressBar: "linear-gradient(90deg, #CC0A4C 0%, #B03083 70%, #F92B81 100%)",
        timerProgressStripes:
          "linear-gradient(-45deg,rgba(255,255,255,.20) 25%,transparent 25%,transparent 50%,rgba(255,255,255,.20) 50%,rgba(255,255,255,.20) 75%,transparent 75%,transparent)",

        card: "radial-gradient(46.81% 58.96% at 50% 0%, #45408D 0%, #151240 100%)",
        flipClock: "linear-gradient(180deg, #57534E 0%, #44403C 42%, #292524 46.5%, #1C1917 100%)",
        grdHigh:
          "radial-gradient(71.88% 55.56% at 50% 0%, theme('backgroundColor.gradientTopHigh') 0%, theme('backgroundColor.gradientBottomHigh') 100%)",
        grdMid:
          "radial-gradient(69.19% 53.57% at 50% 0%, theme('backgroundColor.gradientTopMid') 0%, theme('backgroundColor.gradientBottomMid') 100%)",
        grdLow:
          "radial-gradient(69.19% 53.57% at 50% 0%, theme('backgroundColor.gradientTopLow') 0%, theme('backgroundColor.gradientBottomLow') 100%)",
      },
      backgroundColor: {
        backdrop: "#0C0A09D9",
        overlay: "#000000CC",

        solidLow: "#1C1917",
        gradientTopLow: "#44403C",
        gradientBottomLow: "#0C0A09",

        solidMid: "#292524",
        gradientTopMid: "#44403C",
        gradientBottomMid: "#1C1917",

        solidHigh: "#44403C",
        gradientTopHigh: "#44403C",
        gradientBottomHigh: "#292524",
      },
    },
  },
  plugins: [import("@tailwindcss/typography")],
};
